import { WIZARD_STEPS, stepLabel, type CustomPayRunResponse, type WizardStepKey } from '../types';
import { completedSteps, stepAccess } from './stepAccess';

interface Props {
  run: CustomPayRunResponse;
  activeStep: WizardStepKey;
  onSelect: (step: WizardStepKey) => void;
  /** The viewer is another user who can approve the run (did not prepare or submit it): the third step reads "Approve Payroll". */
  approverView?: boolean;
}

/** The 4-step progress indicator: the active step and completed steps are green (completed ones with a check), the rest
 * grey. A step the run's state does not allow yet is disabled — the backend enforces the transitions, this mirrors them. */
export function WizardStepper({ run, activeStep, onSelect, approverView = false }: Props) {
  const access = stepAccess(run);
  const done = completedSteps(run);
  return (
    <nav aria-label="Custom pay run steps">
      <ol className="cpr-stepper">
        {WIZARD_STEPS.map((s, index) => {
          const active = s.key === activeStep;
          const complete = done.has(s.key) && !active;
          const available = access[s.key].available;
          const className = `cpr-stepper__item${active ? ' cpr-stepper__item--active' : ''}${complete ? ' cpr-stepper__item--done' : ''}`;
          return (
            <li key={s.key} className={className}>
              <button
                type="button"
                className="cpr-stepper__button"
                aria-current={active ? 'step' : undefined}
                disabled={!available}
                title={available ? undefined : access[s.key].reason}
                onClick={() => onSelect(s.key)}
              >
                <span className="cpr-stepper__dot" aria-hidden="true">
                  {complete ? '✓' : index + 1}
                </span>
                <span>
                  {stepLabel(s, approverView)}
                  {complete && <span className="cpr-sr-only"> (completed)</span>}
                </span>
              </button>
              {index < WIZARD_STEPS.length - 1 && <span className="cpr-stepper__line" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
